import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { Close, CreditCard } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createOrder } from "../../../services/order_services";
import { toast } from "react-toastify";


const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiPaper-root": {
    width: "500px",
    maxWidth: "95vw",
    borderRadius: 12,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
}));

const PaymentSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
}));


const getOperatorId = (phoneNumber) => {
  const cleanedNumber = phoneNumber.replace(/\D/g, '');
  const prefix = cleanedNumber.substring(0, 2);
  
  if (['78', '73'].includes(prefix)) return 2;
  if (['76', '77'].includes(prefix)) return 4;
  if (['74'].includes(prefix)) return 1;
  if (['79', '72'].includes(prefix)) return 3;
  if (['70', '71'].includes(prefix)) return 5;
  
  return 2;
};

export default function StripePaymentModal({ open, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [cardName, setCardName] = useState("");
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [requiresAction, setRequiresAction] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  
  const stripe = useStripe();
  const elements = useElements();


  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setStep(1);
    setAmount("");
    setPhone("");
    setCardName("");
    setErrors({});
    setIsProcessing(false);
    setPaymentIntent(null);
    setRequiresAction(false);
    setClientSecret("");
    setCardComplete(false);
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!phone) {
      newErrors.phone = "Please enter a mobile number";
    } else if (isNaN(phone.replace(/\s/g, ''))) {
      newErrors.phone = "Mobile number must contain only numbers";
    } else if (phone.replace(/\s/g, '').length < 9) {
      newErrors.phone = "Please enter a valid mobile number";
    }
    
    if (!amount) {
      newErrors.amount = "Please enter an amount";
    } else if (isNaN(amount)) {
      newErrors.amount = "Amount must be a number";
    } else if (parseFloat(amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!cardName) {
      newErrors.cardName = "Please enter the cardholder name";
    }

    if (!cardComplete) {
      newErrors.card = "Please complete your card details";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      processPayment();
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const { mutate: createOrderMutation, isPending } = useMutation({
    mutationFn: (payload) => createOrder(payload),
    onSuccess: (data) => {
      if (data.status === "requires_action" && data.nextAction) {
        setRequiresAction(true);
        setClientSecret(data.nextAction.clientSecret);
        setPaymentIntent({ id: data.nextAction.paymentIntentId });
      } else if (data.status === "success" || data.status === "inprocess") {
        setStep(4);
        toast.success("Payment processed successfully!");
        onSuccess();
        queryClient.invalidateQueries({ queryKey: ["orders"], exact: false });
      } else {
        setStep(4);
        toast.error(`Payment status: ${data.status}`);
      }
    },
    onError: (error) => {
      setStep(4);
      const message = error?.response?.data?.error || "Payment failed. Please try again.";
      toast.error(message);
      setErrors({ submit: message });
      setIsProcessing(false);
    },
  });

  const handle3DSecureAuthentication = async () => {
    if (!stripe || !clientSecret) return;

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (paymentIntent.status === "succeeded") {
        setStep(4);
        toast.success("Payment processed successfully!");
        onSuccess();
        queryClient.invalidateQueries({ queryKey: ["orders"], exact: false });
      }
    } catch (error) {
      setStep(4);
      toast.error(error.message);
      setErrors({ submit: error.message });
      setIsProcessing(false);
    }
  };

  const processPayment = useCallback(async () => {
    setIsProcessing(true);

    try {
      if (!stripe || !elements) {
        throw new Error("Stripe has not loaded yet.");
      }

      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error("Card element not found.");
      }

    
      const { paymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: cardName,
        },
      });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      const operatorId = getOperatorId(phone);
      const cleanedPhone = phone.replace(/\s/g, '');
      const usdAmount = (parseFloat(amount) * 0.8).toFixed(2);

      const payload = {
        receiver: cleanedPhone,
        amount: parseFloat(amount),
        source: "stripe_card",
        operator: operatorId.toString(),
        currency: "AFN",
        countryId: 1,
        productId: 2,
        cardCurrency: "USD",
        cardAmount: usdAmount,
        paymentMethodId: paymentMethod.id,
        confirmNow: true,
      };

      createOrderMutation(payload);
    } catch (error) {
      setStep(4);
      toast.error(error.message);
      setErrors({ submit: error.message });
      setIsProcessing(false);
    }
  }, [stripe, elements, phone, amount, cardName, createOrderMutation]);

  const renderCardElement = () => (
    <Box mt={2} sx={{ 
      padding: '10px 0', 
      border: '1px solid #c4c4c4', 
      borderRadius: '4px', 
 
      opacity: step === 3 && !requiresAction ? 0.5 : 1 
    }}>
      <CardElement
        options={{
          hidePostalCode: true,
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }}
        onChange={(event) => {
          setCardComplete(event.complete);
          if (event.error) {
            setErrors({ ...errors, card: event.error.message });
          } else {
            const newErrors = { ...errors };
            delete newErrors.card;
            setErrors(newErrors);
          }
        }}
      />
    </Box>
  );

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {step === 1 && "Mobile Top-Up"}
            {step === 2 && "Payment Details"}
            {step === 3 && "Processing Payment"}
            {step === 4 && "Payment Result"}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isProcessing}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {step === 1 && (
          <Box>
            <Typography variant="body2" color="textSecondary" paragraph>
              Enter the mobile number and amount you want to top up
            </Typography>
            
            <TextField
              fullWidth
              label="Mobile Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone}
              margin="normal"
              placeholder="e.g. 787710623"
            />
            
            <TextField
              fullWidth
              label="Amount (AFN)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={!!errors.amount}
              helperText={errors.amount}
              margin="normal"
              InputProps={{
                endAdornment: <InputAdornment position="end">AFN</InputAdornment>,
              }}
            />
            
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2">
                Estimated charge: <strong>${(parseFloat(amount || 0) * 0.012).toFixed(2)} USD</strong>
                <br />
                <small>Exchange rate: 1 AFN ≈ 0.012 USD</small>
              </Typography>
            </Box>
          </Box>
        )}
        
        {(step === 2 || step === 3) && (
          <Box>
            <Typography variant="body2" color="textSecondary" paragraph>
              {step === 2 ? "Enter your card details for payment" : "Processing your payment..."}
            </Typography>
            
            <TextField
              fullWidth
              label="Cardholder Name"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              error={!!errors.cardName}
              helperText={errors.cardName}
              margin="normal"
              disabled={step === 3}
            />
            
            {renderCardElement()}
            {errors.card && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.card}
              </Typography>
            )}
            
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2">
                You will be charged: <strong>${(parseFloat(amount || 0) * 0.012).toFixed(2)} USD</strong>
              </Typography>
            </Box>
          </Box>
        )}
        
        {step === 3 && requiresAction && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              3D Secure Authentication Required
            </Typography>
            <Typography variant="body2" paragraph>
              Your card issuer requires additional authentication. Please complete the verification process.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handle3DSecureAuthentication}
              fullWidth
            >
              Complete Authentication
            </Button>
          </Box>
        )}
        
        {step === 4 && (
          <Box>
            {errors.submit ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.submit}
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                Payment processed successfully!
              </Alert>
            )}
            
            <Box p={2} border={1} borderColor="divider" borderRadius={1}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Recipient:</Typography>
                <Typography variant="body2">{phone}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Amount:</Typography>
                <Typography variant="body2">{amount} AFN</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Charged:</Typography>
                <Typography variant="body2">
                  ${(parseFloat(amount || 0) * 0.012).toFixed(2)} USD
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Date:</Typography>
                <Typography variant="body2">
                  {new Date().toLocaleDateString()}
                </Typography>
              </Box>
              {paymentIntent && (
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2">Payment ID:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {paymentIntent.id}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        {step === 1 && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleNext}
              disabled={!phone || !amount}
            >
              Continue to Payment
            </Button>
          </>
        )}
        
        {step === 2 && (
          <>
            <Button onClick={handleBack}>Back</Button>
            <Button 
              variant="contained" 
              onClick={handleNext}
              disabled={isProcessing || !cardComplete}
              startIcon={isProcessing ? <CircularProgress size={16} /> : <CreditCard />}
            >
              {isProcessing ? 'Processing...' : 'Pay Now'}
            </Button>
          </>
        )}
        
        {step === 4 && (
          <Button 
            variant="contained" 
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
}